import axios from "axios";

const API = process.env.REACT_APP_API_ROOT;

// More than this many selected rows and the counts cover the first ones only —
// one request per id, and a confirm dialog doesn't need to wait on hundreds.
const MAX_COUNTED = 25;

// The backend's label for a book's own summaries. They are DELETED with the
// book rather than edited, so they get their own line.
const OWN_SUMMARIES = "summaries (book name)";
// Posts for which a deleted tag is their only one. They keep their genres but
// need re-tagging, so this is a warning rather than a "removed from" count.
const LEFT_UNTAGGED = "posts left with no tags";
// Sources whose delete goes to the Trash (backend trashService). Taxonomy —
// genres, tags, pillars, professions — is still deleted outright.
const TRASHED = new Set(["book", "author", "series"]);

/**
 * Where these records' names are copied: `{ books: 12, series: 3 }`, summed
 * across `ids`. Null when it couldn't be checked.
 */
export const fetchUsage = async (source, ids, headers) => {
    try {
        const responses = await Promise.all(
            ids.slice(0, MAX_COUNTED).map((id) =>
                axios.get(`${API}/api/admin/references/${source}/${id}/usage`, { headers })
            )
        );
        const total = {};
        responses.forEach(({ data }) => {
            Object.entries(data?.data || {}).forEach(([label, n]) => {
                total[label] = (total[label] || 0) + n;
            });
        });
        return total;
    } catch (error) {
        console.error("fetchUsage catch error::", error);
        return null;
    }
};

/**
 * Asks before a delete that cascades (backend `cascadeService`): says what it
 * will also be removed from, so nobody finds out from the toast afterwards.
 *
 *   if (!(await confirmCascadeDelete({ source: "genre", ids: [id], what: `the genre "${name}"`, headers }))) return;
 */
export const confirmCascadeDelete = async ({ source, ids, what, headers, note = "" }) => {
    const usage = await fetchUsage(source, ids, headers);

    const lines = [`Delete ${what}?${note ? ` ${note}` : ""}`];

    if (usage === null) {
        lines.push("Couldn't check where it is used — it will still be removed from everything that references it.");
    } else {
        const { [OWN_SUMMARIES]: ownSummaries, [LEFT_UNTAGGED]: untagged, ...rest } = usage;
        const removedFrom = Object.entries(rest).map(([label, n]) => `${n} ${label}`).join(", ");
        if (removedFrom) lines.push(`It will also be removed from: ${removedFrom}.`);
        if (untagged) lines.push(`${untagged} ${untagged === 1 ? "post has" : "posts have"} no other tag and will be left untagged — they keep their genres, but should be re-tagged.`);
        if (ownSummaries) lines.push(`${ownSummaries} ${ownSummaries === 1 ? "summary" : "summaries"} of it will be deleted too.`);
        if (ids.length > MAX_COUNTED) lines.push(`(Counted for the first ${MAX_COUNTED} of ${ids.length}.)`);
    }

    lines.push(
        TRASHED.has(source)
            ? "It goes to the Trash, and can be restored from there — with everything above — for 30 days."
            : "This can't be undone."
    );

    // eslint-disable-next-line no-alert
    return window.confirm(lines.join("\n\n"));
};
